import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const QuizPoolSettings = () => {
  const [settings, setSettings] = useState({
    isEnabled: false,
    questionsPerDay: 5,
    maxAttempts: null, // NEW: Maximum attempts per day
    categories: []
  });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchSettings(),
        fetchCategories(),
        fetchCategoryCounts()
      ]);
    } catch (err) {
      setError('Failed to load data. Please refresh the page.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/pool-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data._id) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching pool settings:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
        window.location.href = '/admin/login';
      }
      // Keep default settings if fetch fails
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableCategories(response.data);
      } else {
        setDefaultCategories();
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setDefaultCategories();
    }
  };

  const setDefaultCategories = () => {
    setAvailableCategories([
      { _id: 'default-1', name: 'general', displayName: 'General Sports' },
      { _id: 'default-2', name: 'football', displayName: 'Football' },
      { _id: 'default-3', name: 'cricket', displayName: 'Cricket' },
      { _id: 'default-4', name: 'basketball', displayName: 'Basketball' },
      { _id: 'default-5', name: 'tennis', displayName: 'Tennis' },
      { _id: 'default-6', name: 'hockey', displayName: 'Hockey' },
      { _id: 'default-7', name: 'rugby', displayName: 'Rugby' },
      { _id: 'default-8', name: 'golf', displayName: 'Golf' },
      { _id: 'default-9', name: 'boxing', displayName: 'Boxing' },
      { _id: 'default-10', name: 'other', displayName: 'Other' }
    ]);
  };

  const fetchCategoryCounts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_BASE_URL}/api/admin/quiz/pool-questions-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setCategoryCounts(response.data);
      } else {
        setCategoryCounts([]);
      }
    } catch (error) {
      console.error('Error fetching category counts:', error);
      setCategoryCounts([]);
    }
  };

  const handleAddCategory = () => {
    setSettings({
      ...settings,
      categories: [...settings.categories, { categoryName: '', questionCount: 1 }]
    });
  };

  const handleRemoveCategory = (index) => {
    const newCategories = settings.categories.filter((_, i) => i !== index);
    setSettings({ ...settings, categories: newCategories });
  };

  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...settings.categories];
    
    if (field === 'questionCount') {
      const numValue = parseInt(value) || 1;
      const maxAvailable = getCategoryAvailableCount(newCategories[index].categoryName);
      newCategories[index][field] = Math.min(Math.max(1, numValue), maxAvailable || 999);
    } else {
      newCategories[index][field] = value;
      if (field === 'categoryName') {
        const maxAvailable = getCategoryAvailableCount(value);
        newCategories[index].questionCount = Math.min(newCategories[index].questionCount, maxAvailable || 1);
      }
    }
    
    setSettings({ ...settings, categories: newCategories });
  };

  const validateForm = () => {
    if (!settings.isEnabled) {
      return true;
    }

    if (settings.categories.length === 0) {
      alert('Please add at least one category when pool mode is enabled.');
      return false;
    }

    const categoryNames = settings.categories.map(c => c.categoryName);
    const duplicates = categoryNames.filter((item, index) => categoryNames.indexOf(item) !== index);
    if (duplicates.length > 0) {
      alert(`Duplicate categories found: ${duplicates.join(', ')}. Please remove duplicates.`);
      return false;
    }

    const hasEmptyCategory = settings.categories.some(c => !c.categoryName);
    if (hasEmptyCategory) {
      alert('Please select a category for all rows or remove empty rows.');
      return false;
    }

    const totalQuestions = settings.categories.reduce((sum, cat) => sum + parseInt(cat.questionCount || 0), 0);
    if (totalQuestions !== settings.questionsPerDay) {
      alert(`Total questions from categories (${totalQuestions}) must equal questions per day (${settings.questionsPerDay})`);
      return false;
    }

    for (const cat of settings.categories) {
      const available = getCategoryAvailableCount(cat.categoryName);
      if (available > 0 && cat.questionCount > available) {
        const categoryInfo = availableCategories.find(c => c.name === cat.categoryName);
        alert(`Not enough pool questions in ${categoryInfo?.displayName || cat.categoryName}. Available: ${available}, Requested: ${cat.questionCount}`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.post(`${API_BASE_URL}/api/admin/quiz/pool-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        alert('Pool settings saved successfully!');
        setSettings(response.data);
        await fetchCategoryCounts();
      }
    } catch (error) {
      console.error('Error saving pool settings:', error);
      if (error.response?.status === 404) {
        alert('Pool settings endpoint not available. Please configure backend first.');
      } else {
        alert(`Failed to save settings: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const getCategoryAvailableCount = (categoryName) => {
    if (!categoryName) return 0;
    const found = categoryCounts.find(c => c.categoryName === categoryName);
    return found ? found.questionCount : 0;
  };

  const totalSelectedQuestions = settings.categories.reduce(
    (sum, cat) => sum + parseInt(cat.questionCount || 0), 
    0
  );

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-600">Loading pool settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border-l-4 border-red-500 p-4 rounded">
          <p className="font-bold text-red-800">Error</p>
          <p className="text-red-700">{error}</p>
          <button
            onClick={fetchAllData}
            className="mt-3 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Quiz Pool Settings</h2>
        <p className="text-sm text-gray-600 mt-1">Configure random daily quiz questions from a pool</p>
      </div>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-bold text-yellow-800">Important:</p>
            <p className="text-sm text-yellow-700 mt-1">
              When pool mode is enabled, date-specific questions will NOT be shown. 
              Users will receive the same random questions all day, and new questions will appear tomorrow.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border space-y-6">
        <div>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.isEnabled}
              onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div>
              <span className="font-bold text-lg block">Enable Pool Mode</span>
              <span className="text-sm text-gray-600">
                When enabled, questions will be randomly selected from the pool each day
              </span>
            </div>
          </label>
        </div>

        {settings.isEnabled && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Questions Per Day <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={settings.questionsPerDay}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    questionsPerDay: Math.min(Math.max(1, parseInt(e.target.value) || 1), 20)
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Between 1 and 20 questions</p>
              </div>

              {/* NEW: Maximum Attempts Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Attempts Per Day
                </label>
                <input
                  type="number"
                  min="1"
                  value={settings.maxAttempts || ''}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    maxAttempts: e.target.value === '' ? null : parseInt(e.target.value)
                  })}
                  placeholder="Leave empty for unlimited"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {settings.maxAttempts 
                    ? `Users can submit ${settings.maxAttempts} time${settings.maxAttempts > 1 ? 's' : ''} per day`
                    : 'Unlimited attempts per day'
                  }
                </p>
              </div>
            </div>

            <div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
                <h3 className="font-bold text-lg">Category Distribution</h3>
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  + Add Category
                </button>
              </div>

              {settings.categories.length === 0 ? (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <p className="text-gray-500 mb-3">No categories added yet</p>
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    Add Your First Category
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {settings.categories.map((cat, index) => {
                    const availableCount = getCategoryAvailableCount(cat.categoryName);
                    
                    return (
                      <div key={index} className="flex flex-col sm:flex-row gap-3 p-4 border border-gray-300 rounded-lg bg-gray-50">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={cat.categoryName}
                            onChange={(e) => handleCategoryChange(index, 'categoryName', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            <option value="">Select Category</option>
                            {availableCategories.map((category) => (
                              <option key={category._id || category.name} value={category.name}>
                                {category.displayName}
                              </option>
                            ))}
                          </select>
                          {cat.categoryName && (
                            <p className={`text-xs mt-1 ${availableCount === 0 ? 'text-red-600' : 'text-gray-600'}`}>
                              {availableCount === 0 ? (
                                <>⚠️ No pool questions available in this category</>
                              ) : (
                                <>Available pool questions: <strong>{availableCount}</strong></>
                              )}
                            </p>
                          )}
                        </div>
                        
                        <div className="w-full sm:w-32">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Questions <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            min="1"
                            max={availableCount || 999}
                            value={cat.questionCount}
                            onChange={(e) => handleCategoryChange(index, 'questionCount', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={!cat.categoryName || availableCount === 0}
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">Max: {availableCount || 0}</p>
                        </div>
                        
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveCategory(index)}
                            className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {settings.categories.length > 0 && (
                <div className={`mt-4 p-4 rounded-lg ${
                  totalSelectedQuestions === settings.questionsPerDay 
                    ? 'bg-green-50 border border-green-300' 
                    : 'bg-red-50 border border-red-300'
                }`}>
                  <div className="flex items-center justify-between">
                    <p className="font-bold">
                      Total Questions: <span className={totalSelectedQuestions === settings.questionsPerDay ? 'text-green-600' : 'text-red-600'}>
                        {totalSelectedQuestions}
                      </span> / {settings.questionsPerDay}
                    </p>
                    {totalSelectedQuestions === settings.questionsPerDay ? (
                      <span className="text-green-600 font-bold">✓ Valid</span>
                    ) : (
                      <span className="text-red-600 font-bold">✗ Invalid</span>
                    )}
                  </div>
                  {totalSelectedQuestions !== settings.questionsPerDay && (
                    <p className="text-red-600 text-sm mt-2">
                      ⚠️ Total must equal {settings.questionsPerDay}. 
                      {totalSelectedQuestions > settings.questionsPerDay 
                        ? ` Remove ${totalSelectedQuestions - settings.questionsPerDay} question(s).`
                        : ` Add ${settings.questionsPerDay - totalSelectedQuestions} more question(s).`
                      }
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={saving}
          className="w-full sm:w-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-bold transition-colors"
        >
          {saving ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : (
            'Save Pool Settings'
          )}
        </button>
      </form>

      <div className="mt-8 bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <h3 className="font-bold text-lg mb-4">Available Pool Questions by Category</h3>
        {categoryCounts.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categoryCounts.map((cat) => (
              <div 
                key={cat.categoryName} 
                className="p-4 border border-gray-300 rounded-lg text-center bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-md transition-shadow"
              >
                <p className="font-bold text-gray-800 truncate" title={cat.displayName}>
                  {cat.displayName}
                </p>
                <p className="text-3xl font-bold text-blue-600 my-2">{cat.questionCount}</p>
                <p className="text-sm text-gray-600">question{cat.questionCount !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 text-lg font-medium">No pool questions available yet</p>
            <p className="text-gray-400 text-sm mt-2">Go to Quiz Management and add some pool questions first.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizPoolSettings;
